// src/pages/Admin/Views/CategoryManagement/FlagDefinitionsPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../../components/ui/button'; // 
import { Input } from '../../../../components/ui/input'; // 
import { Label } from '../../../../components/ui/label'; // 
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card'; // 
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select'; // 
import { useToast } from '../../../../hooks/use-toast';

import api from "../../../../services/apiServices"; // adjust path as needed

// This should match the FlagType enum in your backend
const flagTypes = [
    "urgent", "business_lead", "remote_service",
    "language", "budget_tier", "project_scale", "quality_risk"
];

export default function FlagDefinitionsPage() {
    const [labels, setLabels] = useState([]);
    const [newLabel, setNewLabel] = useState('');
    const [selectedFlagType, setSelectedFlagType] = useState(flagTypes[0]);
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();


    const fetchLabels = useCallback(async () => {
    setIsLoading(true);
    try {
        const res = await api.get(`/admin/catgeory-flags/flag-definitions`);
        setLabels(res.data);
    } catch (error) {
        toast({
        variant: "destructive",
        title: "Error",
        description:
            error?.response?.data?.message || error.message || "Failed to fetch definitions.",
        });
    } finally {
        setIsLoading(false);
    }
    }, [toast]);


    useEffect(() => {
        fetchLabels();
    }, [fetchLabels]);

    const handleAddLabel = async (e) => {
    e.preventDefault();

    if (!newLabel.trim()) {
        toast({ title: "Validation Error", description: "Label cannot be empty." });
        return;
    }

    try {
        await api.post(`/admin/catgeory-flags/flag-definitions`, {
        flag_type: selectedFlagType,
        label: newLabel,
        });

        toast({ title: "Success", description: "Flag definition added." });
        setNewLabel('');
        fetchLabels(); // Refresh the list
    } catch (error) {
        toast({
        variant: "destructive",
        title: "Error adding label",
        description: error?.response?.data?.detail || error.message || 'Failed to add label',
        });
    }
    };

    
    const handleDelete = async (labelId) => {
        if (!window.confirm("Are you sure you want to delete this definition?")) return;

        try {
            await api.delete(`/admin/catgeory-flags/flag-definitions/${labelId}`);
            toast({ title: "Success", description: "Flag definition deleted." });
            fetchLabels(); // Refresh the list
        } catch (error) {
            toast({
            variant: "destructive",
            title: "Error deleting label",
            description: error?.response?.data?.message || error.message || 'Failed to delete label',
            });
        }
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold">Manage Flag Definitions</h1>
            <p className="text-muted-foreground">
                Define the allowed output labels for categorized flags like 'budget_tier'.
            </p>

            <Card>
                <CardHeader>
                    <CardTitle>Add New Definition</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleAddLabel} className="flex flex-col md:flex-row items-end gap-4">
                        <div className="flex-1 w-full">
                            <Label htmlFor="flagType">Flag Type</Label>
                            <Select value={selectedFlagType} onValueChange={setSelectedFlagType}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a flag type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {flagTypes.map(type => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1 w-full">
                            <Label htmlFor="newLabel">Output Label</Label>
                            <Input
                                id="newLabel"
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                placeholder="e.g., 'high', 'medium', 'low'"
                            />
                        </div>
                        <Button type="submit">Add Definition</Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Existing Definitions</CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <p>Loading...</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="p-4">Flag Type</th>
                                        <th className="p-4">Label</th>
                                        <th className="p-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {labels.map((label) => (
                                        <tr key={label.id} className="border-b">
                                            <td className="p-4 font-medium">{label.flag_type}</td>
                                            <td className="p-4">{label.label}</td>
                                            <td className="p-4 text-right">
                                                <Button variant="destructive" size="sm" onClick={() => handleDelete(label.id)}>
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}